import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Trash2, Plus, Minus, ArrowRight, ShoppingBag } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';

const CartPage = () => {
  const navigate = useNavigate();
  const { cartItems, updateQuantity, removeFromCart, getCartTotal, clearCart } = useCart();
  const { isAuthenticated, login } = useAuth();

  const formatPrice = (price) => {
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: 'TRY',
      minimumFractionDigits: 0,
    }).format(price);
  };

  const subtotal = getCartTotal();
  const shippingCost = subtotal >= 500 ? 0 : 50;
  const total = subtotal + shippingCost;

  const handleCheckout = () => {
    // Redirect directly to Shopier for payment
    window.open('https://www.shopier.com/valmeraofficial', '_blank', 'noopener,noreferrer');
  };

  if (cartItems.length === 0) {
    return (
      <div className="pt-32 pb-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center py-20">
            <ShoppingBag size={64} className="mx-auto mb-6 text-gray-300" />
            <h1 className="text-3xl font-light tracking-wider mb-4">SEPETİNİZ BOŞ</h1>
            <p className="text-gray-500 mb-8">Henüz sepetinize ürün eklemediniz.</p>
            <Link
              to="/urunler"
              className="inline-flex items-center gap-2 bg-black text-white px-10 py-4 text-sm tracking-wider hover:bg-gray-900 transition-colors"
            >
              ALIŞVERİŞE BAŞLA <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-32 pb-20">
      <div className="max-w-7xl mx-auto px-6">
        <h1 className="text-4xl font-light tracking-wider mb-12">ALIŞVERİŞ SEPETİ</h1>

        <div className="grid lg:grid-cols-3 gap-12">
          {/* Cart Items */}
          <div className="lg:col-span-2">
            <div className="border-b pb-4 mb-4 hidden md:grid grid-cols-12 gap-4 text-sm font-medium tracking-wider">
              <div className="col-span-6">ÜRÜN</div>
              <div className="col-span-2 text-center">FİYAT</div>
              <div className="col-span-2 text-center">ADET</div>
              <div className="col-span-2 text-right">TOPLAM</div>
            </div>

            <div className="space-y-6">
              {cartItems.map((item) => (
                <div
                  key={`${item.id}-${item.selectedSize}-${item.selectedColor}`}
                  className="grid md:grid-cols-12 gap-4 items-center pb-6 border-b"
                >
                  {/* Product */}
                  <div className="md:col-span-6 flex gap-4">
                    <Link to={`/urun/${item.id}`} className="w-24 h-24 flex-shrink-0">
                      <img
                        src={item.image}
                        alt={item.name}
                        className="w-full h-full object-contain"
                      />
                    </Link>
                    <div>
                      <Link
                        to={`/urun/${item.id}`}
                        className="font-medium hover:underline"
                      >
                        {item.name}
                      </Link>
                      <p className="text-sm text-gray-500 mt-1">{item.selectedSize} cm</p>
                      {item.selectedColor && (
                        <p className="text-sm text-gray-500">{item.selectedColor}</p>
                      )}
                      <button
                        onClick={() => removeFromCart(item.id, item.selectedSize, item.selectedColor)}
                        className="flex items-center gap-1 text-sm text-gray-400 hover:text-red-500 mt-2 transition-colors"
                      >
                        <Trash2 size={14} />
                        Kaldır
                      </button>
                    </div>
                  </div>

                  {/* Price */}
                  <div className="md:col-span-2 text-center">
                    <span className="md:hidden text-sm text-gray-500">Fiyat: </span>
                    {formatPrice(item.price)}
                  </div>

                  {/* Quantity */}
                  <div className="md:col-span-2 flex items-center justify-center">
                    <div className="flex items-center border border-gray-300">
                      <button
                        onClick={() =>
                          updateQuantity(item.id, item.selectedSize, item.selectedColor, item.quantity - 1)
                        }
                        className="p-2 hover:bg-gray-50 transition-colors"
                      >
                        <Minus size={14} />
                      </button>
                      <span className="px-4 text-center min-w-[40px]">{item.quantity}</span>
                      <button
                        onClick={() =>
                          updateQuantity(item.id, item.selectedSize, item.selectedColor, item.quantity + 1)
                        }
                        className="p-2 hover:bg-gray-50 transition-colors"
                      >
                        <Plus size={14} />
                      </button>
                    </div>
                  </div>

                  {/* Total */}
                  <div className="md:col-span-2 text-right font-medium">
                    <span className="md:hidden text-sm text-gray-500">Toplam: </span>
                    {formatPrice(item.price * item.quantity)}
                  </div>
                </div>
              ))}
            </div>

            <div className="flex items-center justify-between mt-8">
              <Link
                to="/urunler"
                className="text-sm tracking-wider hover:underline"
              >
                ← Alışverişe Devam Et
              </Link>
              <button
                onClick={clearCart}
                className="text-sm tracking-wider text-gray-500 hover:text-black transition-colors"
              >
                Sepeti Temizle
              </button>
            </div>
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-gray-50 p-6">
              <h2 className="text-lg font-light tracking-wider mb-6">SİPARİŞ ÖZETİ</h2>

              <div className="space-y-4 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-gray-500">Ara Toplam</span>
                  <span>{formatPrice(subtotal)}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-500">Kargo</span>
                  <span className={shippingCost === 0 ? 'text-green-600' : ''}>
                    {shippingCost === 0 ? 'Ücretsiz' : formatPrice(shippingCost)}
                  </span>
                </div>
                {shippingCost > 0 && (
                  <p className="text-xs text-gray-500">
                    500₺ üzeri siparişlerde kargo ücretsiz!
                  </p>
                )}
                <div className="flex items-center justify-between pt-4 border-t text-base">
                  <span className="font-medium">Toplam</span>
                  <span className="font-medium text-lg">{formatPrice(total)}</span>
                </div>
              </div>

              <button 
                data-testid="checkout-shopier-btn"
                onClick={handleCheckout}
                className="w-full bg-black text-white py-4 text-sm tracking-wider hover:bg-gray-900 transition-colors mt-6"
              >
                SHOPIER İLE SATIN AL
              </button>

              {/* Promo Code */}
              <div className="mt-6">
                <label className="text-sm text-gray-500 block mb-2">Promosyon Kodu</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    placeholder="Kodu girin"
                    className="flex-1 border border-gray-300 px-4 py-2 text-sm focus:outline-none focus:border-black transition-colors"
                  />
                  <button className="bg-gray-200 text-black px-4 py-2 text-sm hover:bg-gray-300 transition-colors">
                    UYGULA
                  </button>
                </div>
              </div>

              {/* Payment Icons */}
              <div className="mt-8 pt-6 border-t">
                <p className="text-xs text-gray-500 text-center mb-4">Güvenli Ödeme - Shopier</p>
                <div className="flex items-center justify-center gap-4">
                  <div className="w-10 h-6 bg-gray-200 rounded flex items-center justify-center text-xs">VISA</div>
                  <div className="w-10 h-6 bg-gray-200 rounded flex items-center justify-center text-xs">MC</div>
                  <div className="w-10 h-6 bg-gray-200 rounded flex items-center justify-center text-xs">TROY</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CartPage;
